import { FormEvent, useEffect, useState } from 'react';
import { initialsFromName } from '../mentorDashboardFormat';
import MenteePortalService, { MenteePairMessage } from './MenteePortalService';

type Props = {
    open: boolean;
    onClose: () => void;
    onSent: () => void;
    /** Real mentor identity for the modal header. Omitted -> neutral copy, never a placeholder. */
    mentorName?: string | null;
    mentorRole?: string | null;
};

/**
 * Dashboard message plumbing (DEC-P16-003) — modal over GET/POST pairs/messages.
 * Not the Messages tab (unknown ticket).
 */
const MenteeDashboardThreadModal = ({ open, onClose, onSent, mentorName, mentorRole }: Props) => {
    const headerName = (mentorName || '').trim();
    const headerRole = (mentorRole || '').trim();
    const [messages, setMessages] = useState<MenteePairMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        MenteePortalService.listPairMessages()
            .then((data) => {
                if (cancelled) return;
                setMessages(Array.isArray(data.messages) ? data.messages : []);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setError(MenteePortalService.errorMessage(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    if (!open) {
        return null;
    }

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        setError(null);
        MenteePortalService.sendPairMessage(body)
            .then((msg) => {
                setMessages((prev) => [...prev, msg]);
                setDraft('');
                onSent();
            })
            .catch((err: unknown) => {
                setError(MenteePortalService.errorMessage(err));
            })
            .finally(() => setSending(false));
    };

    return (
        <div className="mdash-modal-backdrop" role="presentation" onClick={onClose}>
            <div
                className="mdash-modal p-0"
                role="dialog"
                aria-modal="true"
                aria-labelledby="mdash-thread-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mdash-modal-head px-4 py-3 bg-secondary bg-opacity-10">
                    <div className="d-flex flex-wrap flex-sm-nowrap align-items-center gap-2">
                        <span className="d-flex align-items-center justify-content-center rounded-circle border border-primary border-opacity-25 flex-shrink-0 bg-primary bg-opacity-10 font-600" style={{ width: '40px', height: '40px' }}>
                            {initialsFromName(headerName) || '—'}
                        </span>
                        <div className="flex-grow-1">
                            <h2 id="mdash-thread-title" className="mdash-modal-title font-600 text-capitalize">
                                {headerName || 'Your mentor'}
                            </h2>
                            {headerRole ? <p className="mdash-modal-sub">{headerRole}</p> : null}
                        </div>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
                {loading ? <p className="mreg-sub">Loading thread…</p> : null}
                {error ? <p className="mreg-banner is-danger">{error}</p> : null}
                <ul className="mdash-thread p-4 mb-0" aria-label="Messages">
                    {[...messages].reverse().map((msg) => (
                        <li
                            key={msg.id}
                            className={`mdash-bubble${
                                String(msg.senderRole).toUpperCase() === 'MENTEE' ? ' is-me' : ''
                            }`}
                        >
                            <span className="mdash-bubble-role">{msg.senderRole}</span>
                            <p className="mdash-bubble-body">{msg.body}</p>
                        </li>
                    ))}
                    {!loading && messages.length === 0 ? (
                        <li className="mdash-thread-empty">No messages yet. Send the first one.</li>
                    ) : null}
                </ul>
                <form className="mdash-compose mdash-modal-head mb-0 px-4 py-3 bg-white" onSubmit={onSubmit}>
                    <textarea
                        className="form-control"
                        rows={3}
                        maxLength={4000}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Write a message…"
                        disabled={sending}
                    />
                    <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={sending || !draft.trim()}
                    >
                        {sending ? 'Sending…' : 'Send'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default MenteeDashboardThreadModal;
